/**
 * Wasserij-wachtrij — verstuurt voorinspectie-, reinigen- en na-inspectie-
 * saves die bij een netwerkfout op het toestel bleven staan.
 *
 * Tot 03-11 bestond dit niet: de drie schermen zetten élke fout in een Dexie-
 * tabel, gingen door alsof het gelukt was, en niemand las die tabellen ooit.
 * Omdat de eigen server-paden sinds kaart 9 ook nog een antwoord zonder
 * `success` gaven, belandde zelfs elke geslaagde save daar. Een echte fout (wifi
 * weg, een weigering) was zo onzichtbaar: het kleed bleef weken op Drogen of
 * Klaar voor Reinigen staan terwijl de medewerker dacht dat het klaar was
 * (werkbank #144/#159, #122).
 *
 * Regels:
 *  - Een weigering van de server (4xx) komt nooit in de wachtrij; het scherm
 *    toont hem (`isServerWeigering`).
 *  - Een rij wordt alleen verstuurd als het kleed nog in dezelfde werklijst
 *    staat. Staat het er niet meer, dan is het al verwerkt (door iemand anders
 *    of een eerdere poging) en vervalt de rij. Zo zet een late save een kleed
 *    nooit terug.
 *  - Rijen ouder dan MAX_LEEFTIJD_MS worden niet meer blind verstuurd maar
 *    gemeld als mislukt.
 *  - Rijen van vóór deze versie (zonder `v`) zijn de dode wachtrij van kaart 9:
 *    vrijwel allemaal al verwerkte saves. Die worden opgeruimd, niet verstuurd.
 */

import type { Table } from 'dexie';
import { salesforceClient, SalesforceApiError } from '../salesforce';
import type { SaveVoorinspectieRequest } from '../salesforce/types';
import {
  db,
  WACHTRIJ_VERSIE,
  removeFromVoorinspectieCache,
  removeCachedReinigenItem,
  removeFromNaInspectieCache,
  type PendingVoorinspectie,
  type PendingReinigen,
  type PendingNaInspectie,
} from '../db/database';

const MAX_LEEFTIJD_MS = 48 * 3_600_000;

/** Server antwoordde en weigerde: tonen, niet opnieuw proberen. 408/429 zijn tijdelijk. */
export function isServerWeigering(err: unknown): err is SalesforceApiError {
  return (
    err instanceof SalesforceApiError &&
    err.statusCode >= 400 &&
    err.statusCode < 500 &&
    err.statusCode !== 401 &&
    err.statusCode !== 408 &&
    err.statusCode !== 429
  );
}

type Rij = PendingVoorinspectie | PendingReinigen | PendingNaInspectie;

interface Soort<R extends Rij> {
  naam: string;
  tabel: Table<R, number>;
  /** Item-ids die nog op deze handeling wachten. */
  wachtend: () => Promise<Set<string>>;
  verstuur: (r: R) => Promise<{ success?: boolean; message?: string }>;
  wisCache: (itemId: string) => Promise<void>;
}

const VOORINSPECTIE: Soort<PendingVoorinspectie> = {
  naam: 'voorinspectie',
  tabel: db.pendingVoorinspecties,
  wachtend: async () => {
    const r = await salesforceClient.getVoorinspectieItems();
    if (!r.success || !r.data) throw new Error(r.message || 'voorinspectielijst niet geladen');
    // De lijst toont ook kleden met een lopend risico waarvan de voorinspectie
    // al gedaan is; die horen niet nog eens opgeslagen te worden.
    return new Set(
      r.data.items.filter((i) => !i.risicoStatus || i.risicoStatus === 'Geen').map((i) => i.itemId)
    );
  },
  verstuur: (r) =>
    salesforceClient.saveVoorinspectie(r.itemId, {
      lengthCm: r.lengthCm,
      widthCm: r.widthCm,
      diameterCm: r.diameterCm,
      isRoundRug: r.isRoundRug,
      vorm: r.vorm as SaveVoorinspectieRequest['vorm'],
      aantal: r.aantal,
      voorinspectie: r.voorinspectie,
      soort: r.soort,
      merk: r.merk,
      voorinspectieOpmerkingen: r.voorinspectieOpmerkingen,
      wasrapport: r.wasrapport,
      markComplete: r.markComplete,
    }),
  wisCache: removeFromVoorinspectieCache,
};

const REINIGEN: Soort<PendingReinigen> = {
  naam: 'reinigen',
  tabel: db.pendingReinigen,
  wachtend: async () => {
    const r = await salesforceClient.getReinigenItems();
    if (!r.success || !r.data) throw new Error(r.message || 'reinigenlijst niet geladen');
    return new Set(r.data.items.map((i) => i.itemId));
  },
  verstuur: (r) =>
    salesforceClient.saveReinigen(r.itemId, {
      hoeReinigen: r.hoeReinigen,
      wasrapport: r.wasrapport,
      markComplete: r.markComplete,
    }),
  wisCache: removeCachedReinigenItem,
};

const NA_INSPECTIE: Soort<PendingNaInspectie> = {
  naam: 'na-inspectie',
  tabel: db.pendingNaInspecties,
  wachtend: async () => {
    const r = await salesforceClient.getNaInspectieItems();
    if (!r.success || !r.data) throw new Error(r.message || 'na-inspectielijst niet geladen');
    return new Set(r.data.items.map((i) => i.itemId));
  },
  verstuur: (r) =>
    salesforceClient.saveNaInspectie(r.itemId, {
      actie: r.actie,
      opmerkingenAflevering: r.opmerkingenAflevering,
      opmerkingenAfleveringOverig: r.opmerkingenAfleveringOverig,
      naFotosGemaakt: r.naFotosGemaakt,
      reden: r.reden,
      redenTekst: r.redenTekst,
    }),
  wisCache: removeFromNaInspectieCache,
};

const SOORTEN = [VOORINSPECTIE, REINIGEN, NA_INSPECTIE] as Array<Soort<Rij>>;

let bezig = false;

async function verwerkSoort(soort: Soort<Rij>): Promise<void> {
  const rijen = await soort.tabel.toArray();
  const oud = rijen.filter((r) => r.v !== WACHTRIJ_VERSIE).map((r) => r.id!);
  if (oud.length > 0) {
    await soort.tabel.bulkDelete(oud);
    console.info(`[wasserij-wachtrij] ${soort.naam}: ${oud.length} oude rij(en) opgeruimd`);
  }
  const open = rijen.filter((r) => r.v === WACHTRIJ_VERSIE && r._syncStatus === 'pending');
  if (open.length === 0) return;

  const wachtend = await soort.wachtend();
  for (const r of open) {
    if (!wachtend.has(r.itemId)) {
      await soort.tabel.delete(r.id!);
      continue;
    }
    if (Date.now() - new Date(r.createdAt).getTime() > MAX_LEEFTIJD_MS) {
      await soort.tabel.update(r.id!, { _syncStatus: 'failed', lastError: 'Te lang niet verstuurd; doe deze handeling opnieuw.' });
      continue;
    }
    try {
      const res = await soort.verstuur(r);
      if (res.success) {
        await soort.tabel.delete(r.id!);
        await soort.wisCache(r.itemId);
      } else {
        await soort.tabel.update(r.id!, { _syncStatus: 'failed', lastError: res.message || 'Opslaan geweigerd' });
      }
    } catch (err) {
      if (isServerWeigering(err)) {
        await soort.tabel.update(r.id!, { _syncStatus: 'failed', lastError: err.message });
        continue;
      }
      // Netwerk of serverfout: volgende ronde opnieuw.
      console.warn(`[wasserij-wachtrij] ${soort.naam} ${r.itemId} nog niet verstuurd:`, err);
      return;
    }
  }
}

/** Eén ronde over de drie wachtrijen. Veilig om vaak aan te roepen. */
export async function verwerkWasserijWachtrij(): Promise<void> {
  if (bezig || !navigator.onLine) return;
  bezig = true;
  try {
    for (const soort of SOORTEN) {
      try {
        await verwerkSoort(soort);
      } catch (err) {
        console.warn(`[wasserij-wachtrij] ${soort.naam} overgeslagen:`, err);
      }
    }
  } finally {
    bezig = false;
  }
}

export interface MisluktRij {
  soort: string;
  id: number;
  itemName: string | null;
  fout: string;
}

/** Handelingen die niet verstuurd konden worden: de wasserij moet ze opnieuw doen. */
export async function leesMislukt(): Promise<MisluktRij[]> {
  const uit: MisluktRij[] = [];
  for (const soort of SOORTEN) {
    const rijen = await soort.tabel.where('_syncStatus').equals('failed').toArray();
    for (const r of rijen) {
      if (r.v !== WACHTRIJ_VERSIE) continue;
      uit.push({ soort: soort.naam, id: r.id!, itemName: r.itemName ?? null, fout: r.lastError ?? 'Niet verstuurd' });
    }
  }
  return uit;
}

export async function wisMislukt(rij: Pick<MisluktRij, 'soort' | 'id'>): Promise<void> {
  const soort = SOORTEN.find((s) => s.naam === rij.soort);
  if (soort) await soort.tabel.delete(rij.id);
}
